import { Component, EventEmitter, Input, Output, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { AddContactInformationService } from "../../../../core/services/add-contact-information.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ActivatedRoute, Params } from "@angular/router";
import { ClientAddressCustomize } from "../../../../core/models/client/details/clientAddresscustomize";
import { LoaderService } from "../../../../core/services/loader.service";
import { EnumContactType } from "../../../../shared/enums/enumContactType";
import { ClientPurpose } from "../../../../shared/enums/clientPurpose";
import { v4 as uuidv4 } from "uuid";
import { DrawerModule } from "primeng/drawer";
import { CommonModule } from "@angular/common";
import { SelectModule } from "primeng/select";
import { JniToastrService } from "../../../../core/services/jni-toastr.service";
import { ContactDto } from "../../../../core/models/client/addclient/contactDto";
import { AddClientContact } from "../../../../core/models/client/addclient/addClientContact";
import { InputTextModule } from "primeng/inputtext";
import { ToggleSwitchModule } from "primeng/toggleswitch";
import { ButtonModule } from "primeng/button";

@UntilDestroy()
@Component({
  selector: "jni-add-contact-information",
  templateUrl: "./add-contact-information.component.html",
  imports: [
    DrawerModule,
    SelectModule,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    ToggleSwitchModule,
    ButtonModule,
  ],
  styleUrls: ["./add-contact-information.component.scss"],
})
export class AddContactInformationComponent implements OnInit {
  @Input() display!: boolean;
  clientForm: FormGroup;
  @Output() displayChange = new EventEmitter<boolean>();
  @Output() refreshSummary = new EventEmitter<boolean>();
  @Input() clientObject!: ClientAddressCustomize;
  clientId = "";
  isPrimaryOne = "1";
  isPrimaryZero = "0";
  clientContactArray: ContactDto[] = [];
  createNewContact = "Create New";
  updateNewContact = "Edit Contact Information";
  @Input() headingStatus = true;
  @Input() isFromOrdersAddNewBuyer = false;
  @Input() addClientContact: AddClientContact = {} as AddClientContact;
  @Input() customerAccountNumber = "";
  @Output() isContactAdded = new EventEmitter<boolean>();
  @Output() populateContactAddedInfo = new EventEmitter<string>();
  type = [
    {
      id: 1,
      name: "Business",
    },
    {
      id: 2,
      name: "Invoice",
    },
    {
      id: 3,
      name: "Payable",
    },
    {
      id: 4,
      name: "Delivery",
    },
    {
      id: 5,
      name: "Other",
    },
    {
      id: 6,
      name: "Buyer",
    },
  ];
  postingDate: Date = new Date();

  @Input()
  set clientIdFromModal(value: string) {
    this.clientId = value;
  }

  constructor(
    private _toastrService: JniToastrService,
    private _formBuilder: FormBuilder,
    private router: ActivatedRoute,
    private _contactInformationService: AddContactInformationService,
    public _loaderService: LoaderService
  ) {
    this.clientForm = this._formBuilder.group({
      firstName: ["", [Validators.pattern("^[a-zA-Z0-9 ]+|[\u10D0-\u10F0]+$")]],
      lastName: ["", [Validators.pattern("^[a-zA-Z0-9 ]+|[\u10D0-\u10F0]+$")]],
      phoneNumber: ["", [Validators.pattern("^[^a-zaA-Z]+$")]],
      email: [
        "",
        [
          Validators.pattern(
            `^[a-zA-Z0-9._%+-]+(?:'[a-zA-Z0-9._%+-]+)?@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$`
          ),
        ],
      ],
      title: [""],
      type: [""],
      electronicAddressIdD365: [""],
      electronicAddressPhone: [""],
      electronicAddressEmail: [""],
      isPrimary: [false],
    });
    this.router.params.subscribe((params: Params) => {
      this.clientId = params["id"];
    });
  }

  formErrors: Record<string, string> = {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
  };

  validationMessages: Record<string, Record<string, string>> = {
    firstName: {
      pattern: "First name format is not correct.",
    },
    lastName: {
      pattern: "Last name format is not correct.",
    },
    phoneNumber: {
      pattern: "Phone number format is not correct.",
    },
    email: {
      pattern: "Email format is not correct.",
    },
  };

  ngOnInit(): void {
    if (this.clientObject && this.headingStatus !== true) {
      if (Object.keys(this.clientObject).length !== 0) {
        this.patchValueInClientForm(this.clientObject);
        this.headingStatus = false;
        this.clientForm.get("type")?.disable();
      }
    } else {
      this.clientForm.get("type")?.enable();
    }
    //Set the Tupe value to Buyer
    if (this.isFromOrdersAddNewBuyer) {
      this.clientForm.patchValue({
        firstName: this.addClientContact.firstName,
        lastName: this.addClientContact.lastName,
        type: this.addClientContact.type,
      });
    }
  }

  logValidationErrors(group: FormGroup = this.clientForm): void {
    Object.keys(group.controls).forEach((key: string) => {
      const abstractControl = group.get(key);
      if (abstractControl instanceof FormGroup) {
        this.logValidationErrors(abstractControl);
      } else {
        this.formErrors[key] = "";
        if (
          abstractControl &&
          !abstractControl.valid &&
          (abstractControl.touched || abstractControl.dirty)
        ) {
          const messages = this.validationMessages[key];
          for (const errorKey in abstractControl.errors) {
            if (errorKey && messages) {
              this.formErrors[key] += messages[errorKey] + " ";
            }
          }
        }
      }
    });
  }

  onHide() {
    this.displayChange.emit(false);
  }

  addDataInClientInfoArray(
    firstName: string,
    lastName: string,
    locator: string,
    purpose: string,
    isPrimary: string,
    type: string,
    electronicAddress: string
  ) {
    const obj: ContactDto = {
      portalKey: uuidv4(),
      contactName: firstName + " " + lastName,
      contact: locator,
      purpose: parseInt(purpose),
      isPrimary: parseInt(isPrimary),
      type: type == "Phone" ? 1 : 2,
      electronicAddress: electronicAddress ?? "",
      createdByPortal: 0,
    };
    return obj;
  }

  patchValueInClientForm(clientObject: ClientAddressCustomize) {
    const firstName = (clientObject!.name! as string).includes(" ")
      ? (clientObject!.name! as string).substring(
          0,
          (clientObject!.name! as string).indexOf(" ")
        )
      : clientObject?.name;

    const lastName = (clientObject!.name! as string).includes(" ")
      ? (clientObject!.name! as string).substring(
          (clientObject!.name! as string).indexOf(" ") + 1
        )
      : "";

    this.clientForm.patchValue({
      firstName: firstName,
      lastName: lastName,
      phoneNumber: clientObject.phone,
      email: clientObject.email,
      title: clientObject.title,
      type: clientObject.purpose,
      electronicAddressPhone: clientObject.electronicAddressPhone,
      electronicAddressEmail: clientObject.electronicAddressEmail,
      isPrimary:
        (clientObject!.isPrimary! as string).toLowerCase() === "yes"
          ? true
          : false,
    });
  }

  getContactObject(addClientDto: AddClientContact, type: string) {
    this.clientContactArray.push(
      this.addDataInClientInfoArray(
        addClientDto.firstName ?? "",
        addClientDto.lastName ?? "",
        addClientDto.phoneNumber ?? "",
        type,
        addClientDto.isPrimary === true
          ? this.isPrimaryOne
          : this.isPrimaryZero,
        EnumContactType.Phone,
        addClientDto.electronicAddressPhone ?? ""
      ),
      this.addDataInClientInfoArray(
        addClientDto.firstName ?? "",
        addClientDto.lastName ?? "",
        addClientDto.email ?? "",
        type,
        addClientDto.isPrimary === true
          ? this.isPrimaryOne
          : this.isPrimaryZero,
        EnumContactType.Email,
        addClientDto.electronicAddressEmail ?? ""
      )
    );
  }

  onSubmit() {
    this._loaderService.setLoaderState(true);
    const addClientDto = this.clientForm.getRawValue();
    if (!this.validateFormData()) {
      this._loaderService.setLoaderState(false);
      this._toastrService.error(
        "Error",
        "Please enter name and contact information"
      );
      return;
    }
    if (addClientDto.type === "Business") {
      this.getContactObject(addClientDto, ClientPurpose.Business);
    } else if (addClientDto.type === "Invoice") {
      this.getContactObject(addClientDto, ClientPurpose.Invoice);
    } else if (
      addClientDto.type === "Payment" ||
      addClientDto.type === "Payable"
    ) {
      this.getContactObject(addClientDto, ClientPurpose.Payment);
    } else if (addClientDto.type === "Delivery") {
      this.getContactObject(addClientDto, ClientPurpose.Delivery);
    } else if (addClientDto.type === "Other") {
      this.getContactObject(addClientDto, ClientPurpose.Other);
    } else if (addClientDto.type === "Buyer") {
      this.getContactObject(addClientDto, ClientPurpose.Buyer);
    }

    const contactObject = {
      accountNumber: this.isFromOrdersAddNewBuyer
        ? this.customerAccountNumber
        : this.clientId,
      contacts: this.clientContactArray,
    };

    if (this.headingStatus) {
      this._contactInformationService
        .addContactInformationData(contactObject)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            this._loaderService.setLoaderState(false);
            if (res.status.toLowerCase() != "success") {
              this._toastrService.error(
                "Error",
                res.message || "Error In Adding Contacts"
              );
            } else {
              this._toastrService.success(
                "Success",
                res.message || "Contact added Successfully"
              );
              this.isContactAdded.emit(true);
              this.populateContactAddedInfo.emit(
                addClientDto?.firstName + " " + addClientDto?.lastName
              );
            }
            this.displayChange.emit(false);
            this.refreshSummary.emit();
          },
          error: res => {
            this.refreshSummary.emit();
            this._loaderService.setLoaderState(false);
            this._toastrService.error(
              "Error",
              res.message || "Error adding contact"
            );
          },
        });
    } else {
      this._contactInformationService
        .upsertContactInformationData(contactObject)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            this._loaderService.setLoaderState(false);
            if (res.status.toLowerCase() != "success") {
              this._toastrService.error(
                "Error",
                res.message || "Error In Updating Contacts"
              );
            } else {
              this._toastrService.success(
                "Success",
                res.message || "Contact Updated SuccessFully"
              );
            }
            this.displayChange.emit(false);
            this.refreshSummary.emit();
          },
          error: res => {
            this.refreshSummary.emit();
            this._loaderService.setLoaderState(false);
            this._toastrService.error(
              "Error",
              res.message || "Error updating contact"
            );
          },
        });
    }
  }
  validateFormData() {
    const addClientDto = this.clientForm.getRawValue();
    if (
      (addClientDto.firstName || addClientDto.lastName) &&
      (addClientDto.phoneNumber || addClientDto.email)
    ) {
      return true;
    } else {
      return false;
    }
  }
}
